import type { KeyboardEvent, SyntheticEvent } from 'react';
import React, { useRef, useState } from 'react';
import { Image } from '@noths/polaris-client-ribbons-design-system';
import { getProductImageURL, isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';

import { VideoPlayIndicator } from 'src/components/atoms/VideoPlayIndicator/VideoPlayIndicator';
import { PRODUCT_MEDIA_SECTION_LABEL_PLURAL } from 'src/components/organisms/ProductMediaCarousel/constants/copy';
import { PRODUCT_MEDIA_TABPANEL_ID_PREFIX } from 'src/components/organisms/ProductMediaCarousel/constants/elementIds';
import {
  mediaKey,
  VIDEO_THUMBNAIL_WIDTH,
  videoPosterUrl,
} from 'src/components/organisms/ProductMediaCarousel/media/buildProductMedia';
import type {
  MediaItem,
  MediaLocation,
  NextOrPreviousPayload,
  ProductMediaState,
  SetCurrentMediaPayload,
} from 'src/components/organisms/ProductMediaCarousel/types';
import { useThumbnailFocusAndScroll } from './useThumbnailFocusAndScroll';
import * as styles from './Thumbnails.styles';

export interface ThumbnailsStateProps {
  currentMedia: ProductMediaState['currentMedia'];
  media: MediaItem[];
}

export interface ThumbnailsDispatchProps {
  onNextMedia: (payload: NextOrPreviousPayload) => void;
  onPrevMedia: (payload: NextOrPreviousPayload) => void;
  setCurrentMedia: (payload: SetCurrentMediaPayload) => void;
}

interface ThumbnailsOwnProps {
  location: MediaLocation;
}

type ThumbnailsProps = ThumbnailsStateProps & ThumbnailsDispatchProps & ThumbnailsOwnProps;

export const Thumbnails = ({
  currentMedia,
  location,
  media,
  onNextMedia,
  onPrevMedia,
  setCurrentMedia,
}: ThumbnailsProps) => {
  const idPrefix = location.toLowerCase().replace(/\s/g, '');
  const totalMedia = media.length;
  const thumbnailRefs = useRef<HTMLButtonElement[] | null[]>([]);
  const [thumbnailsHaveKeyboardFocus, setThumbnailsHaveKeyboardFocus] = useState(false);
  const prevMediaKey = 'ArrowLeft';
  const nextMediaKey = 'ArrowRight';

  useThumbnailFocusAndScroll({
    thumbnailsHaveKeyboardFocus,
    thumbnailRefs,
    currentImage: currentMedia,
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case prevMediaKey:
        onPrevMedia({ location, totalMedia });
        event.preventDefault();
        break;
      case nextMediaKey:
        onNextMedia({ location, totalMedia });
        event.preventDefault();
        break;
    }
  };

  const handleThumbnailFocus = (event: SyntheticEvent<HTMLElement>) =>
    setThumbnailsHaveKeyboardFocus(isKeyboardTriggeredEvent(event));

  const handleClick = (mediaIndex: number) => {
    setCurrentMedia({
      mediaIndex,
      navigationType: 'click',
      location,
    });
  };

  return (
    <div
      aria-label={PRODUCT_MEDIA_SECTION_LABEL_PLURAL}
      aria-orientation="horizontal"
      css={[styles.root, thumbnailsHaveKeyboardFocus ? styles.thumbnailsWithKeyboardFocus : null]}
      onBlur={() => setThumbnailsHaveKeyboardFocus(false)}
      onFocus={handleThumbnailFocus}
      role="tablist"
    >
      <ul css={styles.list}>
        {media.map((item, i) => {
          const isVideo = item.type === 'video';
          const key = mediaKey(item);

          return (
            <li css={styles.listItem} key={key}>
              <button
                aria-controls={`${idPrefix}${PRODUCT_MEDIA_TABPANEL_ID_PREFIX}${i}`}
                aria-label={`${item.title}, ${i + 1} of ${totalMedia}`}
                aria-selected={i === currentMedia}
                css={styles.imageButton}
                onClick={() => handleClick(i)}
                onKeyDown={handleKeyDown}
                ref={(elem) => {
                  thumbnailRefs.current[i] = elem;
                }}
                role="tab"
                tabIndex={i === currentMedia ? undefined : -1}
                type="button"
              >
                {isVideo ? (
                  <span css={styles.videoThumbnail}>
                    <img
                      alt={`${item.title}, thumbnail ${i + 1} of ${totalMedia}`}
                      css={styles.videoPoster}
                      src={videoPosterUrl(item.playbackId, VIDEO_THUMBNAIL_WIDTH)}
                    />
                    <VideoPlayIndicator />
                  </span>
                ) : (
                  <Image
                    alt={`${item.title}, thumbnail ${i + 1} of ${totalMedia}`}
                    css={styles.image}
                    src={getProductImageURL(item.href, 'mini')}
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
